import React from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { createEventId } from './event-utils'
import withClass from '../../../../hoc/withClass'
import styled from "@emotion/styled";
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material'
import authContext from '../../../../context/auth-context'
import axios from 'axios'
import Spinner from '../../../UI/Spinner/Spinner';
import Modal from '../../../UI/Modal/CenterModal/CenterModalNew';
import AddEvent from './AddEvent/AddEvent';
import { useAlert } from 'react-alert'
import { confirmAlert } from 'react-confirm-alert'
import EditEvent from './EditEvent/EditEvent'
import { theme } from 'components/Content/Meetings/Main/Meeting/Details/InProgress/InprogressStepper'
import EditEventWindowDialog from './EditEvent/UI/EditEventWindowDialog'

export const StyleWrapper = styled.div`
  .fc .fc-col-header-cell-cushion {
    padding-top: 0;
    padding-bottom: 0;
  }
  .fc .fc-button{
    padding: 0 8px
  }
  .fc .fc-theme-standard td, .fc-theme-standard th{
    border: 1px solid var(--fc-border-color, #e1e1e1);
  }
  .fc .fc-event{
    font-weight: 400 !important
  }
`

const CustomCalendar = () => {
  const currentEvents = React.useRef([]);
  const { accessToken, companyId, tokenIsValid } = React.useContext(authContext);
  const [editEvent, setEditEvent] = React.useState(null)
  const [openModal, setOpenModal] = React.useState(false);
  const [selectedDay, setSelectedDay] = React.useState(null);
  const calendar = React.useRef(null);
  const event = React.useRef(null);
  const [reloading, setReloading] = React.useState(false)
  const formRef = React.useRef(null);
  const [meeting, setMeeting] = React.useState(null);
  const [isLocked, setIsLocked] = React.useState(true)
  React.useEffect(() => {
    if (tokenIsValid) {
      setReloading(true);
    }
  }, [accessToken, companyId, tokenIsValid]);

  const alert = useAlert();

  // Helper function to get color based on status
  const getStatusColor = (status) => {
    const colorMap = {
      "Draft": "#6c757d",
      "Upcoming": "#3177ad",
      "Postponed": "#3177ad", 
      "In-progress": "#dea704",
      "Completed": "#28a745",
      "Cancelled": "#dc3545",
      "Virtual": "#17a2b8",
      "Virtual/Hybrid": "#17a2b8",
      "Hybrid": "#17a2b8"
    };
    return colorMap[status] || "#6c757d";
  }

  // ================================== | EVENT ACTIONS | ================================== //
  const move = async (id, body) => {
    return await axios.post(`/utilities/company-events/meeting/move/${id}`, body);
  };

  const cancelEvent = async (eventId) => {
    return await axios.put(`/utilities/company-events/meeting/cancel/${eventId}`, {
      reason: "Cancelled from calendar"
    });
  };

  const restoreEvent = async (eventId) => {
    return await axios.put(`/utilities/company-events/meeting/restore/${eventId}`);
  };

  const newEventHandler = event => {
    axios.post('/utilities/company-events/meeting', event)
      .then(response => {
        if (calendar.current) {
          const { message, data } = response.data;
          if (data && Array.isArray(currentEvents.current)) {
            let idList = currentEvents.current.map(a => a.id);
            let newId = Math.max(...idList) + 1;
            let calendarApi = calendar.current
            calendarApi.unselect()
            calendarApi.addEvent({
              id: newId,
              title: data.title,
              event_id: data.id,
              start: new Date(data.start),
              end: new Date(data.end),
              status: data.status || "Draft",
              color: getStatusColor(data.status || "Draft"),
              allDay: false,
            });
          }
          alert.show(message, { type: 'success' });
          window.$('#newEventModal').modal('hide');
        }
      })
      .catch((error) => {
        console.log(error, "The error");
        alert.show("Failed to create event", { type: 'error' });
      })
  }

  const events = React.useMemo(() => currentEvents.current, [reloading])

  const handleMove = ({ event, revert }, isResize) => {
    const start = new Date(event.start);
    const today = new Date()
    if (start.setHours(0, 0, 0, 0) - today.getTime() <= -127721479) {
      revert()
      return;
    }
    confirmAlert({
      customUI: ({ onClose }) => (
        <Paper
          sx={{
            minHeight: "max-content",
            maxWidth: 400,
            borderRadius: 2,
            padding: 4,
          }}
          elevation={1}
        >
          <Stack direction="column" spacing={2}>
            <Typography sx={{ fontFamily: 'inherit' }} variant="h5">
              {isResize ? `Change ${event.title} event time` : `Move ${event.title}`}
            </Typography>
            <Typography sx={{ fontFamily: 'inherit' }} variant="body">
              {isResize ? 'Change event time?' : `Move event to ${event.start.toDateString()}?`}
            </Typography>
            <Stack direction="row" justifyContent="space-between" >
              <Button
                variant="outlined"
                color="error"
                size='small'
                onClick={() => {
                  revert();
                  onClose();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => {
                  move(event.extendedProps.event_id, {
                    startDate: event.start,
                    endDate: event.end,
                  })
                    .then((res) => {
                      if (res.data) {
                        onClose();
                        alert.show(`${event.title} moved to ${event.start.toDateString()}`, {
                          type: "success",
                        });
                      } else {
                        revert();
                        onClose();
                      }
                    })
                    .catch(() => {
                      revert();
                      onClose();
                    });
                }}
              >
                {isResize ? "Change Time" : "Move"}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ),
      closeOnEscape: false,
      closeOnClickOutside: false,
      overlayClassName: "react-confirm-alert-custom-overlay",
    });
  }

  const handleEventClick = (clickInfo) => {
    const eventStatus = clickInfo.event.extendedProps.status;
    const eventId = clickInfo.event.extendedProps.event_id;
    const eventTitle = clickInfo.event.title;

    // If event is cancelled, show restore option
    if (eventStatus === "Cancelled") {
      confirmAlert({
        customUI: ({ onClose }) => (
          <Paper
            sx={{
              minHeight: "max-content",
              maxWidth: 400,
              borderRadius: 2,
              padding: 4,
            }}
            elevation={1}
          >
            <Stack direction="column" spacing={2}>
              <Typography sx={{ fontFamily: 'inherit' }} variant="h5">
                Restore Meeting
              </Typography>
              <Typography sx={{ fontFamily: 'inherit' }} variant="body">
                Restore "{eventTitle}" meeting?
              </Typography>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Button
                  variant="outlined"
                  color="error"
                  size='small'
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => {
                    restoreEvent(eventId)
                      .then((response) => {
                        if (response.data) {
                          setReloading(true);
                          alert.show("Meeting restored successfully", { type: "success" });
                        }
                      })
                      .catch((error) => {
                        alert.show("Failed to restore meeting", { type: "error" });
                      });
                    onClose();
                  }}
                >
                  Restore
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ),
        closeOnEscape: false,
        closeOnClickOutside: false,
      });
      return;
    }

    // For non-cancelled events, show action options
    confirmAlert({
      customUI: ({ onClose: onCloseMain }) => (
        <Paper
          sx={{
            minHeight: "max-content",
            maxWidth: 400,
            borderRadius: 2,
            padding: 4,
          }}
          elevation={1}
        >
          <Stack direction="column" spacing={2}>
            <Typography sx={{ fontFamily: 'inherit' }} variant="h5">
              Meeting Actions
            </Typography>
            <Typography sx={{ fontFamily: 'inherit' }} variant="body">
              Choose action for "{eventTitle}"
            </Typography>
            <Stack direction="column" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  const eventObj = {
                    id: createEventId(),
                    event_id: eventId,
                    title: eventTitle,
                    start: clickInfo.event.start,
                    end: clickInfo.event.end,
                    allDay: false
                  };
                  event.current = clickInfo.event;
                  setEditEvent(eventObj);
                  setOpenModal(true);
                  onCloseMain();
                }}
              >
                Edit Meeting
              </Button>
              
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={() => {
                  confirmAlert({
                    customUI: ({ onClose }) => (
                      <Paper
                        sx={{
                          minHeight: "max-content",
                          maxWidth: 400,
                          borderRadius: 2,
                          padding: 4,
                        }}
                        elevation={1}
                      >
                        <Stack direction="column" spacing={2}>
                          <Typography sx={{ fontFamily: 'inherit' }} variant="h5">
                            Cancel Meeting
                          </Typography>
                          <Typography sx={{ fontFamily: 'inherit' }} variant="body">
                            Cancel "{eventTitle}" meeting?
                          </Typography>
                          <Stack direction="row" justifyContent="space-between" spacing={2}>
                            <Button
                              variant="outlined"
                              color="primary"
                              size='small'
                              onClick={onClose}
                            >
                              No, Keep
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => {
                                cancelEvent(eventId)
                                  .then((response) => {
                                    if (response.data) {
                                      setReloading(true);
                                      alert.show("Meeting cancelled successfully", { type: "success" });
                                    }
                                  })
                                  .catch((error) => {
                                    alert.show("Failed to cancel meeting", { type: "error" });
                                  });
                                onClose();
                                onCloseMain();
                              }}
                            >
                              Yes, Cancel
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    ),
                    closeOnEscape: false,
                    closeOnClickOutside: false,
                  });
                }}
              >
                Cancel Meeting
              </Button>
              
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={onCloseMain}
              >
                Close
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ),
      closeOnEscape: false,
      closeOnClickOutside: false,
    });
  }

  React.useEffect(() => {
    if (tokenIsValid) {
      if (!reloading) return;
      const response = axios.get('utilities/company-events');
      response.then(res => {
        let events = [];
        const { data } = res;
        if (Array.isArray(data)) {
          events = [...data?.map((event, index) => {
            return {
              id: index + 1,
              event_id: event.id,
              title: event.title,
              start: new Date(event.start),
              end: new Date(event.end),
              color: event.statusColor,
              status: event.status
            }
          })];
        }
        currentEvents.current = [...events]
        setReloading(false)
      }).catch(err => {
        console.log('ERROR')
        setReloading(false)
      })
    } else {
      currentEvents.current = []
    }
    return () => {
      setEditEvent(null)
      currentEvents.current = []
    }
  }, [accessToken, companyId, tokenIsValid, reloading]);

  const handleDateSelect = (selectInfo) => {
    const today = new Date();
    if (Math.sign(selectInfo.start.getTime() - today.getTime() + 84400000) === -1) return;
    calendar.current = selectInfo.view.calendar
    window.$('#newEventModal').modal('show')
    setSelectedDay(selectInfo.start)
  }

  React.useEffect(() => {
    if (editEvent && openModal && event.current)
      window.$('#editEventModal').modal('show')
  }, [editEvent, openModal])

  const handleEvents = (events) => {
    currentEvents.current = [...events]
  }

  const renderEventContent = (eventInfo) => {
    const isCancelled = eventInfo.event.extendedProps.status === "Cancelled";
    
    return (
      <Grid container direction="row">
        <Grid item xs={4}>
          <Typography 
            sx={{ 
              fontFamily: 'inherit',
              textDecoration: isCancelled ? 'line-through' : 'none',
              opacity: isCancelled ? 0.7 : 1
            }}
            color="common.white"
            noWrap
            variant='inherit'
          >
            {eventInfo.timeText}
          </Typography>
        </Grid>
        <Grid item xs={8}>
          <Typography 
            sx={{ 
              fontFamily: 'inherit',
              textDecoration: isCancelled ? 'line-through' : 'none',
              opacity: isCancelled ? 0.7 : 1
            }}
            color="common.white"
            noWrap
            variant='inherit'
          >
            {eventInfo.event.title}
          </Typography>
        </Grid>
      </Grid>
    )
  }

  const eventDidMount = (info) => {
    const isCancelled = info.event.extendedProps.status === "Cancelled";
    
    if (isCancelled) {
      info.el.style.textDecoration = "line-through";
      info.el.style.opacity = "0.7";
      info.el.style.background = "#dc3545";
      info.el.style.borderColor = "#dc3545";
      return;
    }

    switch (info.event.extendedProps.status) {
      case "Upcoming":
      case "Postponed":
        info.el.style.background = "#3177ad";
        info.el.style.borderColor = "#3177ad";
        break;
      case "Draft":
        info.el.style.background = "#6c757d";
        info.el.style.borderColor = "#6c757d";
        break;
      case "In-progress":
        info.el.style.background = "#dea704";
        info.el.style.borderColor = "#dea704";
        break;
      case "Completed":
        info.el.style.background = "#28a745";
        info.el.style.borderColor = "#28a745";
        break;
      case "Virtual":
      case "Virtual/Hybrid":
      case "Hybrid":
        info.el.style.background = "#17a2b8";
        info.el.style.borderColor = "#17a2b8";
        break;
      default:
        info.el.style.background = "#6c757d";
        info.el.style.borderColor = "#6c757d";
        break;
    }
  }

  return (
    <ThemeProvider theme={theme}>
      {!reloading ? (
        <div style={{ padding: '30px 10px 10px' }}>
          <StyleWrapper>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay'
              }}
              height="80vh"
              initialView='dayGridMonth'
              editable={true}
              selectable={true}
              selectMirror={false}
              dayMaxEvents={true}
              events={events}
              select={handleDateSelect}
              businessHours
              eventContent={renderEventContent}
              eventClick={handleEventClick}
              eventDidMount={eventDidMount}
              eventsSet={handleEvents}
              eventResize={(info) => handleMove(info, true)}
              eventDrop={(info) => handleMove(info, false)}
            />
          </StyleWrapper>
          
          <Modal modalId="newEventModal" modalTitle="New Event">
            <AddEvent
              createHandler={data => { newEventHandler(data); window.$('#newEventModal').modal('hide'); }}
              eventDate={selectedDay}
            />
          </Modal>
          
          <EditEventWindowDialog
            open={openModal}
            setOpen={setOpenModal}
            formRef={formRef}
            meeting={meeting}
            isLocked={isLocked}
            setIsLocked={setIsLocked}
          >
            {(editEvent && event.current) && (
              <EditEvent
                editEvent={editEvent}
                event={event}
                formRef={formRef}
                setOpenModal={setOpenModal}
                meeting={meeting}
                setMeeting={setMeeting}
                isLocked={isLocked}
              />
            )}
          </EditEventWindowDialog>
        </div>
      ) : (
        <Box height='80vh' width='100%'>
          <Spinner module='calendar' />
        </Box>
      )}
    </ThemeProvider>
  )
}

export default withClass(CustomCalendar, 'page-main');
